/**
 * Lightweight, privacy-conscious analytics helper.
 *
 * ReaderFLEX does not currently bundle a dedicated analytics SDK. This helper
 * forwards events to whichever provider happens to be present on the page
 * (Google Analytics / gtag, Google Tag Manager dataLayer, or Plausible) and
 * safely no-ops otherwise. This keeps event instrumentation in place and
 * future-proof without hard-coding a specific vendor.
 *
 * IMPORTANT: Never pass personally identifiable information (names, emails,
 * free-text feedback, etc.) into these events. Only pass non-personal
 * properties such as book id, title, category, and UI state.
 */

const isProd = process.env.NODE_ENV === 'production';

export function trackEvent(eventName, properties = {}) {
    if (!eventName) return;

    try {
        // Google Analytics 4 / gtag
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', eventName, properties);
        }

        // Google Tag Manager dataLayer
        if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
            window.dataLayer.push({ event: eventName, ...properties });
        }

        // Plausible custom events
        if (typeof window !== 'undefined' && typeof window.plausible === 'function') {
            window.plausible(eventName, { props: properties });
        }

        // Development visibility only. Never logs in production.
        if (!isProd && typeof console !== 'undefined') {
            // eslint-disable-next-line no-console
            console.debug('[analytics]', eventName, properties);
        }
    } catch (_) {
        // Analytics must never break the page.
    }
}

let _pendingTrack = null;

export function trackServerEvent(eventType, metadata = {}) {
    if (!eventType) return;
    try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        if (_pendingTrack) _pendingTrack.abort();
        const controller = new AbortController();
        _pendingTrack = controller;

        fetch(`${baseURL}/usage/track`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ eventType, metadata }),
            signal: controller.signal,
            credentials: 'include',
        }).catch(() => {}).finally(() => { _pendingTrack = null; });
    } catch (_) {
        // Server tracking must never break the page.
    }
}

export default trackEvent;
